// create component ExerciseCategoryEditForm to edit ExerciseCategory
import { useActionState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { State, updateExercise } from "../actions";
import FormBody from "../../../../components/form-body";
import Label from "../../../../components/label";
import Input from "../../../../components/input";
import FormError from "../../../../components/form-error";
import { Button } from "../../../../components/button";
import {
  Exercise,
  ExerciseCategory,
  ExerciseLoadType,
} from "../../../../models";
import Toggle from "../../../../components/toggle";
import Select from "../../../../components/select";
import Textarea from "../../../../components/textarea";

export default function ExerciseEditForm({
  exercise,
  categories,
  loadTypes,
}: {
  exercise: Exercise;
  categories: ExerciseCategory[];
  loadTypes: ExerciseLoadType[];
}) {
  const navigate = useNavigate();
  const initialState: State = {
    message: null,
    success: false,
    errors: {},
    defaultValues: {
      name: exercise.name,
      orderNumber: exercise.orderNumber,
      description: exercise.description,
      exerciseCategoryId: exercise.exerciseCategoryId,
      exerciseLoadTypeId: exercise.exerciseLoadTypeId,
      isActive: exercise.isActive,
      isFree: exercise.isFree,
    },
  };

  const updateWithId = updateExercise.bind(null, exercise.id!);
  const [state, formAction, isPending] = useActionState(
    updateWithId,
    initialState
  );
  useEffect(() => {
    if (state.success) {
      navigate("/admin/exercises");
    }
  }, [state]);

  return (
    <form className="space-y-6 max-w-xl py-8 text-left" action={formAction}>
      <FormBody>
        <div className="mb-4">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter name"
            aria-describedby="name-error"
            defaultValue={state.defaultValues?.name ?? ""}
            required
          />
          <FormError id="name-error" message={state.errors?.name} />
        </div>

        <div className="mb-4">
          <Label htmlFor="exerciseCategoryId">Exercise Category</Label>
          <Select
            id="exerciseCategoryId"
            name="exerciseCategoryId"
            placeholder="Select exercise category"
            defaultValue={state.defaultValues?.exerciseCategoryId ?? ""}
            aria-describedby="exerciseCategoryId-error"
            items={categories.map((item) => ({
              id: item.id,
              name: item.name,
            }))}
            required
          ></Select>

          <FormError
            id="exerciseCategoryId-error"
            message={state.errors?.exerciseCategoryId}
          />
        </div>

        <div className="mb-4">
          <Label htmlFor="exerciseLoadTypeId">Exercise Load</Label>
          <Select
            id="exerciseLoadTypeId"
            name="exerciseLoadTypeId"
            placeholder="Enter exercise load"
            aria-describedby="exerciseLoadTypeId-error"
            defaultValue={state.defaultValues?.exerciseLoadTypeId ?? ""}
            items={loadTypes.map((item) => ({
              id: item.id,
              name: item.name,
            }))}
            required
          ></Select>
          <FormError
            id="exerciseLoadTypeId-error"
            message={state.errors?.exerciseLoadTypeId}
          />
          <div className="mb-4">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              placeholder="Enter description"
              aria-describedby="description-error"
              defaultValue={state.defaultValues?.description ?? ""}
            />
            <FormError
              id="description-error"
              message={state.errors?.description}
            />
          </div>
        </div>
        <div className="mb-4">
          <Label htmlFor="orderNumber">Order Number</Label>
          <Input
            id="orderNumber"
            name="orderNumber"
            type="number"
            step="1"
            min={1}
            placeholder="Enter order number"
            aria-describedby="orderNumber-error"
            defaultValue={state.defaultValues?.orderNumber ?? ""}
            required
          />
          <FormError
            id="orderNumber-error"
            message={state.errors?.orderNumber}
          />
        </div>
        <div className="flex gap-10 mb-4">
          <div className="">
            <Label htmlFor="isActive">Is Active</Label>
            <Toggle
              id="isActive"
              name="isActive"
              aria-describedby="isActive-error"
              checked={state.defaultValues?.isActive ?? false}
            ></Toggle>

            <FormError id="isActive-error" message={state.errors?.isActive} />
          </div>
          <div className="">
            <Label htmlFor="isActive">Is Free</Label>
            <Toggle
              id="isFree"
              name="isFree"
              aria-describedby="isFree-error"
              checked={state.defaultValues?.isFree ?? false}
            ></Toggle>

            <FormError id="isFree-error" message={state.errors?.isFree} />
          </div>
        </div>
      </FormBody>
      <div className="mt-6 flex justify-end gap-4">
        <Link to="/admin/exercises">
          <Button type="reset" disabled={isPending}>
            Cancel
          </Button>
        </Link>
        <Button
          type="submit"
          disabled={isPending}
          loading={isPending}
          className="min-w-[90px]"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
}
